import React, { useState, useCallback, useEffect } from 'react';

import {
  ChoiceWrapper,
  ClickableChoice,
  ChoiceLabelValueWrapper,
  ChoiceLabel,
  ChoiceValue,
  ChoiceAdditionalInfosWrapper,
  ChoiceAdditionalInfosLabel,
  ChoiceAdditionalInfosValue,
  ChoiceRadio,
  ChoiceCheckbox,
  ButtonsWrapper,
  RedButton,
  BlackButton,
  ChoicesListTitle
} from '../styles/components';
import { ChoiceListOption } from '../../funnel/types';
import LegalNotice from 'shared/components/presentational/LegalNotice';

interface ChoicesListProps<T extends ChoiceListOption> {
  type: 'unique' | 'multiple';
  title?: string;
  choices?: T[];
  initialChoices?: T[];
  cancelButtonLabel?: string;
  validateButtonLabel?: string;
  onCancelClick?: () => void;
  onValidateClick?: (choices: T[]) => void;
  hideCodes?: boolean;
  legalNoticeTitle?: string;
  legalNoticePictogram?: string;
  legalNotiveLink?: string;
  smallLabel?: boolean;
  toPhone?: boolean;
}

export default function ChoicesList<T extends ChoiceListOption>({
  type,
  title,
  choices,
  initialChoices,
  cancelButtonLabel,
  validateButtonLabel,
  onCancelClick,
  onValidateClick,
  hideCodes = true,
  legalNoticeTitle,
  legalNoticePictogram,
  legalNotiveLink,
  smallLabel,
  toPhone
}: ChoicesListProps<T>) {
  const [touched, setTouched] = useState(false);
  const [selectedChoices, setSelectedChoices] = useState<T[]>(
    choices?.filter(c => initialChoices?.some(ic => ic.code === c.code)) ?? []
  );

  // setting initial value of selectedChoices once getting it if no value is yet set
  useEffect(() => {
    if (!selectedChoices.length && !touched) {
      const found = choices?.filter(c =>
        initialChoices?.some(ic => ic.code === c.code)
      );

      if (found?.length) {
        setSelectedChoices(found);
      }
    }
  }, [selectedChoices, touched, choices, initialChoices]);

  const handleChoiceClick = useCallback(
    (choice: T) => {
      if (!touched) {
        setTouched(true);
      }

      if (type === 'unique') {
        setSelectedChoices([choice]);
      } else if (type === 'multiple') {
        selectedChoices.some(c => c.code === choice.code)
          ? setSelectedChoices(
              selectedChoices.filter(c => c.code !== choice.code)
            )
          : setSelectedChoices([...selectedChoices, choice]);
      }
    },
    [touched, type, selectedChoices]
  );

  const handleValidateClick = useCallback(() => {
    onValidateClick && onValidateClick(selectedChoices);
  }, [onValidateClick, selectedChoices]);

  return (
    <>
      {title && <ChoicesListTitle>{title}</ChoicesListTitle>}

      {choices?.map(choice => (
        <ChoiceWrapper key={choice.code} withHPadding withBorderB>
          <ClickableChoice onClick={() => handleChoiceClick(choice)}>
            {typeof choice.additionalCode !== 'undefined' && (
              <ChoiceAdditionalInfosWrapper>
                {choice.additionalLabel && (
                  <ChoiceAdditionalInfosLabel>
                    {choice.additionalLabel}
                  </ChoiceAdditionalInfosLabel>
                )}

                <ChoiceAdditionalInfosValue>
                  {choice.additionalCode}{' '}
                  {choice.additionalCodePrecision && (
                    <small>{choice.additionalCodePrecision}</small>
                  )}
                </ChoiceAdditionalInfosValue>
              </ChoiceAdditionalInfosWrapper>
            )}
            <ChoiceLabelValueWrapper>
              <ChoiceLabel
                withoutMarginB={hideCodes}
                bold={!!(hideCodes || choice.additionalCode) && !smallLabel}
                fontSize={
                  hideCodes || choice.additionalCode ? 'medium' : 'small'
                }
              >
                {choice.label}
              </ChoiceLabel>
              {!hideCodes && <ChoiceValue>{choice.code}</ChoiceValue>}
            </ChoiceLabelValueWrapper>

            {type === 'unique' && (
              <ChoiceRadio
                checked={selectedChoices.some(c => c.code === choice.code)}
              />
            )}
            {type === 'multiple' && (
              <ChoiceCheckbox
                checked={selectedChoices.some(c => c.code === choice.code)}
              />
            )}
          </ClickableChoice>
        </ChoiceWrapper>
      ))}

      {toPhone ? (
        legalNoticeTitle || legalNoticePictogram ? (
          <LegalNotice
            title={legalNoticeTitle}
            pictogram={legalNoticePictogram}
            redirectLink={legalNotiveLink}
            fontSizes={{ title: 16 }}
          />
        ) : null
      ) : null}

      {(onCancelClick || onValidateClick) && (
        <ButtonsWrapper fixedBToPhablet withRadiusBFromPhablet>
          {onValidateClick && (
            <RedButton
              disabled={selectedChoices.length === 0}
              onClick={handleValidateClick}
            >
              <RedButton.children.Text>
                {validateButtonLabel}
              </RedButton.children.Text>
            </RedButton>
          )}

          {onCancelClick && (
            <BlackButton onClick={onCancelClick}>
              <BlackButton.children.Text>
                {cancelButtonLabel}
              </BlackButton.children.Text>
            </BlackButton>
          )}
        </ButtonsWrapper>
      )}
    </>
  );
}
